import React, { useEffect, useState } from 'react';
import { Calendar, Cloud, Trash2, RefreshCw, Check, AlertCircle, ExternalLink, Loader2, ChevronDown, ChevronUp, Info } from 'lucide-react';

const CalendarSettings = () => {
  const [connections, setConnections] = useState([]);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showICloudForm, setShowICloudForm] = useState(false);
  const [iCloudEmail, setICloudEmail] = useState('');
  const [iCloudPassword, setICloudPassword] = useState('');
  const [connectingICloud, setConnectingICloud] = useState(false);
  const [showGoogleInstructions, setShowGoogleInstructions] = useState(false);
  const [showICloudInstructions, setShowICloudInstructions] = useState(false);
  const [expandedConnection, setExpandedConnection] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConnections();
    
    // Check for callback params
    const params = new URLSearchParams(window.location.search);
    const calendarConnected = params.get('calendar_connected');
    const calendarError = params.get('calendar_error');
    
    if (calendarConnected) {
      setSuccess(`${calendarConnected === 'google' ? 'Google' : 'iCloud'} Calendar connected successfully!`);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setSuccess(null), 5000);
    }
    
    if (calendarError) {
      setError(`Failed to connect: ${calendarError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/calendar/connections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setConnections(data.connections || []);
        setAvailableProviders(data.available_providers || []);
      }
    } catch (err) {
      console.error('Failed to fetch calendar connections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/calendar/google/auth-url`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        window.location.href = data.authorization_url;
      } else {
        const errorData = await resp.json();
        setError(errorData.detail || 'Failed to get authorization URL');
      }
    } catch (err) {
      setError('Failed to connect Google Calendar');
    }
  };

  const handleConnectICloud = async (e) => {
    e.preventDefault();
    setConnectingICloud(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/calendar/icloud/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: iCloudEmail,
          app_password: iCloudPassword
        })
      });
      
      if (resp.ok) {
        setSuccess('iCloud Calendar connected successfully!');
        setShowICloudForm(false);
        setICloudEmail('');
        setICloudPassword('');
        fetchConnections();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        const errorData = await resp.json();
        setError(errorData.detail || 'Failed to connect iCloud Calendar');
      }
    } catch (err) {
      setError('Failed to connect iCloud Calendar');
    } finally {
      setConnectingICloud(false);
    }
  };

  const handleSync = async (connectionId) => {
    setSyncing(prev => ({ ...prev, [connectionId]: true }));
    setError(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/calendar/connections/${connectionId}/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setSuccess(`Synced ${data.synced} events`);
        fetchConnections();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await resp.json();
        setError(errorData.detail || 'Sync failed');
      }
    } catch (err) {
      setError('Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this calendar?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/calendar/connections/${connectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (resp.ok) {
        setSuccess('Calendar disconnected');
        fetchConnections();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to disconnect calendar');
    }
  };

  const toggleCalendarSync = async (connectionId, calendarId, currentSyncState) => {
    const connection = connections.find(c => c.connection_id === connectionId);
    if (!connection) return;
    
    const newCalendars = connection.calendars.map(cal => 
      cal.calendar_id === calendarId ? { ...cal, sync: !currentSyncState } : cal
    );
    
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/api/calendar/connections/${connectionId}/calendars`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          calendars: newCalendars.filter(c => c.sync).map(c => c.calendar_id)
        })
      });
      
      fetchConnections();
    } catch (err) {
      console.error('Failed to update calendars:', err);
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google':
        return (
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
        );
      case 'icloud':
        return (
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
            <Cloud className="w-5 h-5 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-lg bg-zinc-700 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-zinc-400" />
          </div>
        );
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">Connected</span>;
      case 'error':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">Error</span>;
      case 'pending':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400">Pending</span>;
      default:
        return <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-400">{status}</span>;
    }
  };

  const googleProvider = availableProviders.find(p => p.id === 'google');
  const icloudProvider = availableProviders.find(p => p.id === 'icloud');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="calendar-settings">
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="w-4 h-4 text-blue-400" />
        <h2 className="text-base font-semibold text-white">Calendar Integration</h2>
      </div>
      <p className="text-zinc-500 text-sm mb-5">
        Connect your calendars to automatically track meetings and receive post-meeting prompts.
      </p>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-green-400 text-sm">
          <Check className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400/60 hover:text-red-400">×</button>
        </div>
      )}

      {/* Connected Calendars */}
      {connections.length > 0 && (
        <div className="space-y-3 mb-6">
          <h3 className="text-sm font-medium text-zinc-400">Connected Calendars</h3>
          {connections.map(conn => (
            <div key={conn.connection_id} className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
              <div 
                className="p-4 flex items-center gap-3 cursor-pointer hover:bg-zinc-800/70"
                onClick={() => setExpandedConnection(expandedConnection === conn.connection_id ? null : conn.connection_id)}
              >
                {getProviderIcon(conn.provider)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {conn.provider === 'google' ? 'Google Calendar' : 'iCloud Calendar'}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">{conn.email}</p>
                </div>
                {getStatusBadge(conn.status)}
                {expandedConnection === conn.connection_id ? (
                  <ChevronUp className="w-4 h-4 text-zinc-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-zinc-500" />
                )}
              </div>
              
              {expandedConnection === conn.connection_id && (
                <div className="px-4 pb-4 border-t border-zinc-700/50 pt-3">
                  {/* Calendars List */}
                  {conn.calendars && conn.calendars.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-zinc-500 mb-2">Calendars to sync:</p>
                      <div className="space-y-1.5">
                        {conn.calendars.map(cal => (
                          <label key={cal.calendar_id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={cal.sync}
                              onChange={() => toggleCalendarSync(conn.connection_id, cal.calendar_id, cal.sync)}
                              className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-zinc-900"
                            />
                            <span className="text-zinc-300">{cal.name}</span>
                            {cal.primary && <span className="text-xs text-zinc-500">(Primary)</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Last Sync Info */}
                  {conn.last_sync && (
                    <p className="text-xs text-zinc-500 mb-3">
                      Last synced: {new Date(conn.last_sync).toLocaleString()}
                    </p>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSync(conn.connection_id); }}
                      disabled={syncing[conn.connection_id]}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 hover:bg-zinc-600 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${syncing[conn.connection_id] ? 'animate-spin' : ''}`} />
                      {syncing[conn.connection_id] ? 'Syncing...' : 'Sync Now'}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDisconnect(conn.connection_id); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Disconnect
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Connect New Calendar */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-zinc-400">
          {connections.length > 0 ? 'Add Another Calendar' : 'Connect a Calendar'}
        </h3>
        
        {/* Google Calendar */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            {getProviderIcon('google')}
            <div className="flex-1">
              <p className="text-sm font-medium text-white">Google Calendar</p>
              <p className="text-xs text-zinc-500">Connect via Google OAuth</p>
            </div>
            {googleProvider?.configured ? (
              <button
                onClick={handleConnectGoogle}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                data-testid="connect-google-btn"
              >
                Connect
              </button>
            ) : (
              <span className="text-xs text-amber-400">Setup required</span>
            )}
          </div>
          
          {!googleProvider?.configured && (
            <div className="mt-2">
              <button
                onClick={() => setShowGoogleInstructions(!showGoogleInstructions)}
                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-300"
              >
                <Info className="w-3.5 h-3.5" />
                {showGoogleInstructions ? 'Hide setup instructions' : 'View setup instructions'}
              </button>
              
              {showGoogleInstructions && (
                <div className="mt-3 p-3 bg-zinc-900 rounded-lg text-xs text-zinc-400 space-y-2">
                  <p className="font-medium text-zinc-300">To enable Google Calendar:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Create a project at <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Cloud Console</a></li>
                    <li>Enable the Google Calendar API</li>
                    <li>Create OAuth 2.0 credentials (Web application)</li>
                    <li>Add the redirect URI to your credentials</li>
                    <li>Set GOOGLE_CALENDAR_CLIENT_ID and GOOGLE_CALENDAR_CLIENT_SECRET in environment</li>
                  </ol>
                  <a 
                    href={`${API_URL}/api/calendar/setup/google`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-400 hover:underline mt-2"
                  >
                    View detailed instructions <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* iCloud Calendar */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            {getProviderIcon('icloud')}
            <div className="flex-1">
              <p className="text-sm font-medium text-white">iCloud Calendar</p>
              <p className="text-xs text-zinc-500">Connect with app-specific password</p>
            </div>
            <button
              onClick={() => setShowICloudForm(!showICloudForm)}
              className="px-4 py-2 text-sm bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
              data-testid="connect-icloud-btn"
            >
              {showICloudForm ? 'Cancel' : 'Connect'}
            </button>
          </div>
          
          {showICloudForm && (
            <form onSubmit={handleConnectICloud} className="mt-4 space-y-4">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Apple ID Email</label>
                <input
                  type="email"
                  value={iCloudEmail}
                  onChange={(e) => setICloudEmail(e.target.value)}
                  placeholder="your@icloud.com"
                  required
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none"
                  data-testid="icloud-email-input"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">App-Specific Password</label>
                <input
                  type="password"
                  value={iCloudPassword}
                  onChange={(e) => setICloudPassword(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  required
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none"
                  data-testid="icloud-password-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Generate at{' '}
                  <a href="https://appleid.apple.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                    appleid.apple.com
                  </a>
                  {' '}→ Security → App-Specific Passwords
                </p>
              </div>
              <button
                type="submit"
                disabled={connectingICloud}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                data-testid="icloud-connect-submit"
              >
                {connectingICloud ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect iCloud'
                )}
              </button>
            </form>
          )}
          
          <button
            onClick={() => setShowICloudInstructions(!showICloudInstructions)}
            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-300 mt-3"
          >
            <Info className="w-3.5 h-3.5" />
            {showICloudInstructions ? 'Hide instructions' : 'How to get an app-specific password'}
          </button>
          
          {showICloudInstructions && (
            <div className="mt-3 p-3 bg-zinc-900 rounded-lg text-xs text-zinc-400 space-y-2">
              <ol className="list-decimal list-inside space-y-1">
                <li>Make sure you have Two-Factor Authentication enabled on your Apple ID</li>
                <li>Go to <a href="https://appleid.apple.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">appleid.apple.com</a></li>
                <li>Sign in with your Apple ID</li>
                <li>Navigate to "Security" section</li>
                <li>Click "App-Specific Passwords" → "Generate"</li>
                <li>Label it "Leadership OS Calendar"</li>
                <li>Copy the generated password (format: xxxx-xxxx-xxxx-xxxx)</li>
              </ol>
              <p className="text-amber-400/80 mt-2">
                Note: Your regular Apple ID password will not work. You must use an app-specific password.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* What happens next */}
      <div className="mt-6 p-4 bg-zinc-800/30 border border-zinc-700/50 rounded-lg">
        <h4 className="text-sm font-medium text-zinc-300 mb-2">What happens when you connect?</h4>
        <ul className="text-xs text-zinc-500 space-y-1.5">
          <li className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
            <span>Your meetings are automatically tracked (past 7 days to future 14 days)</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
            <span>After meetings, you'll receive a quick prompt to capture key takeaways</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
            <span>People from your calendar are automatically linked to your People context</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
            <span>Weekly interaction insights help you see where your time goes</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CalendarSettings;
